// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

type SettingsSortBy = components["schemas"]["SettingsSortByEnum"];
type SettingsChainsSortBy = components["schemas"]["SettingsChainsSortByEnum"];
type SettingsStoresSortBy = components["schemas"]["SettingsStoresSortByEnum"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

// --- Types ---

export interface SettingEntry {
  settingId: number;
  key: string;
  name: string;
  description: string;
  groupName: string;
  fieldType: "boolean" | "number" | "string" | "list" | "json" | "enum";
  enumValues?: string[];
  defaultValue: string;
  chainOverridesCount: number;
  storeOverridesCount: number;
}

export interface SettingChainEntry {
  chainId: number;
  chainName: string;
  legibleName: string;
  overrideId?: number;
  value: string;
  status: string;
}

export interface SettingStoreEntry {
  storeId: number;
  storeName: string;
  legibleName: string;
  chainName: string;
  chainLegibleName: string;
  overrideId?: number;
  value: string;
  status: string;
}

// --- List queries ---

interface SettingsListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: SettingsSortBy;
  orderBy?: SortOrder;
  group?: string;
  onlyChainOverrides?: boolean;
  onlyStoreOverrides?: boolean;
}

export function useSettingsQuery(params: SettingsListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, group, onlyChainOverrides, onlyStoreOverrides } = params;

  return useQuery({
    queryKey: [
      "settings",
      "list",
      { page, limit, search, sortBy, orderBy, group, onlyChainOverrides, onlyStoreOverrides },
    ],
    queryFn: async () => {
      const res = await api.GET("/v1/settings", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[group]": group || undefined,
            "filter[onlyChainOverrides]": onlyChainOverrides,
            "filter[onlyStoreOverrides]": onlyStoreOverrides,
          },
        },
      });
      return res.data as unknown as {
        settings: SettingEntry[];
        count: number;
      };
    },
  });
}

// --- Detail query ---

export function useSettingDetailQuery(settingName: string) {
  return useQuery({
    queryKey: ["settings", "detail", settingName],
    queryFn: async () => {
      const res = await api.GET("/v1/settings/{setting_name}", {
        params: { path: { setting_name: settingName } },
      });
      return res.data as unknown as SettingEntry;
    },
    enabled: !!settingName,
  });
}

// --- Setting chains list ---

interface SettingChainsParams {
  settingName: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: SettingsChainsSortBy;
  orderBy?: SortOrder;
  onlyOverrides?: boolean;
}

export function useSettingChainsQuery(params: SettingChainsParams) {
  const { settingName, page = 1, limit = 20, search, sortBy, orderBy, onlyOverrides } = params;

  return useQuery({
    queryKey: ["settings", settingName, "chains", { page, limit, search, sortBy, orderBy, onlyOverrides }],
    queryFn: async () => {
      const res = await api.GET("/v1/settings/{setting_name}/chains", {
        params: {
          path: { setting_name: settingName },
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[onlyOverrides]": onlyOverrides,
          },
        },
      });
      return res.data as unknown as {
        settings: SettingChainEntry[];
        count: number;
      };
    },
    enabled: !!settingName,
  });
}

// --- Setting stores list ---

interface SettingStoresParams {
  settingName: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: SettingsStoresSortBy;
  orderBy?: SortOrder;
  onlyOverrides?: boolean;
  chainName?: string;
}

export function useSettingStoresQuery(params: SettingStoresParams) {
  const { settingName, page = 1, limit = 20, search, sortBy, orderBy, onlyOverrides, chainName } = params;

  return useQuery({
    queryKey: ["settings", settingName, "stores", { page, limit, search, sortBy, orderBy, onlyOverrides, chainName }],
    queryFn: async () => {
      const res = await api.GET("/v1/settings/{setting_name}/stores", {
        params: {
          path: { setting_name: settingName },
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[onlyOverrides]": onlyOverrides,
            "filter[chainName]": chainName || undefined,
          },
        },
      });
      return res.data as unknown as {
        settings: SettingStoreEntry[];
        count: number;
      };
    },
    enabled: !!settingName,
  });
}

// --- Mutations (chain-level, flexible chainName) ---

export function useEditSettingForChainMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chainName, body }: { chainName: string; body: { overrideId: number; newValue: string } }) => {
      const res = await api.PATCH("/v1/chains/{chain_name}/settings", {
        params: { path: { chain_name: chainName } },
        body: [body],
      });
      if (res.error) throw new Error("Failed to edit chain setting");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useAddSettingForChainMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chainName, body }: { chainName: string; body: { settingId: number; newValue: string } }) => {
      const res = await api.POST("/v1/chains/{chain_name}/settings", {
        params: { path: { chain_name: chainName } },
        body,
      });
      if (res.error) throw new Error("Failed to add chain setting");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useDeleteSettingForChainMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chainName, overrideId }: { chainName: string; overrideId: number }) => {
      const res = await api.DELETE("/v1/chains/{chain_name}/settings", {
        params: { path: { chain_name: chainName } },
        body: [{ overrideId }],
      });
      if (res.error) throw new Error("Failed to restore chain setting");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

// --- Mutations (store-level, flexible storeName) ---

export function useEditSettingForStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ storeName, body }: { storeName: string; body: { overrideId: number; newValue: string } }) => {
      const res = await api.PATCH("/v1/stores/{store_name}/settings", {
        params: { path: { store_name: storeName } },
        body: [body],
      });
      if (res.error) throw new Error("Failed to edit store setting");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useAddSettingForStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ storeName, body }: { storeName: string; body: { settingId: number; newValue: string } }) => {
      const res = await api.POST("/v1/stores/{store_name}/settings", {
        params: { path: { store_name: storeName } },
        body,
      });
      if (res.error) throw new Error("Failed to add store setting");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}

export function useDeleteSettingForStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ storeName, overrideId }: { storeName: string; overrideId: number }) => {
      const res = await api.DELETE("/v1/stores/{store_name}/settings", {
        params: { path: { store_name: storeName } },
        body: [{ overrideId }],
      });
      if (res.error) throw new Error("Failed to restore store setting");
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
    },
  });
}
